import { catchError, map, of, Subject } from 'rxjs'
import { employeeAPIModel } from 'src/app/models/api.model'
import { WebService } from 'src/app/services/api/web.service'
import { convertISOToDate, convertToISO } from 'src/app/services/common/common.service'
import {
  NOMINEE_DOB,
  PASSPORT_VALIDITY_FROM,
  PASSPORT_VALIDITY_TO,
  DATE_OF_LEAVING,
  DATE_OF_JOINING_IN_EPF,
  DOB as EXCEL_DOB,
  DATE_OF_JOINING,
  DATE_OF_EXIT_FROM_PREVIOUS_EMPLOYMENT
} from 'src/app/constants/excel.columns'
import { AbstractValidator } from './abstract-validator.interface'
import { EmployeeService } from 'src/app/services/employee/employee.service'

export class EmployeeValidator extends AbstractValidator<employeeAPIModel> {
  public override allRows: Subject<employeeAPIModel[]> = new Subject<employeeAPIModel[]>()
  public override title = 'Employee Documents'
  public override templateURL: string = ''
  public override saveResponse: Subject<any> = new Subject<any>()
  public bulkImportApiFn: Function = null
  public formatEmployees: Function = null
  public createAddress: Function = null
  public override document: { isRequired: boolean; showUpload: boolean; file$: Subject<File> } = {
    isRequired: false,
    showUpload: false,
    file$: new Subject<File>()
  }

  public override startImporting$: Subject<boolean> = new Subject<boolean>()
  public override apiResponse$: Subject<any> = new Subject<boolean>()
  private rowsToSave = []
  private documentToSave = null
  constructor(public employeeService: EmployeeService, u: employeeAPIModel = null, public webService: WebService) {
    super(u)

    this.allRows.subscribe(rowsToSave => {
      this.rowsToSave = rowsToSave
    })

    this.document.file$.subscribe((file: File) => {
      this.documentToSave = file
    })

    this.subscribeImportStart()
  }

  private subscribeImportStart() {
    this.startImporting$.subscribe(doStartImporting => {
      if (doStartImporting) {
        let employees = this.createEmployeesList()
        employees = this.formatEmployees(employees)
        this.bulkImportApiFn(employees, this.webService)
          .pipe(
            map((response: any) => {
              this.apiResponse$.next({
                status: 1,
                response: {
                  ...{ ...response, dataList: this.convertResponseAsPerExcel(response) },
                  fileName: 'employee.xlsx'
                }
              })
            }),
            catchError((error: any) => {
              this.apiResponse$.next({ status: 0, error })
              return of(null)
            })
          )
          .subscribe()
      }
    })
  }
  employeesData: any = []
  private createEmployeesList(): employeeAPIModel[] {
    this.employeesData = []
    for (const row of this.rowsToSave) {
      for (const key of Object.keys(row)) {
        if (
          (key === NOMINEE_DOB.key ||
            key === PASSPORT_VALIDITY_FROM.key ||
            key === PASSPORT_VALIDITY_TO.key ||
            key === DATE_OF_LEAVING.key ||
            key === DATE_OF_JOINING_IN_EPF.key ||
            key === EXCEL_DOB.key ||
            key === DATE_OF_JOINING.key ||
            key === DATE_OF_EXIT_FROM_PREVIOUS_EMPLOYMENT.key) &&
          row[key]
        ) {
          row[key] = convertToISO(row[key])
        }
      }
      const employee = this.buildEmployee(row)

      this.employeesData.push(employee)
    }
    return this.employeesData
  }

  private buildEmployee(row: any) {
    const employee = {
      employeeWithReferenceDetailsModels: {
        employeeOrchModel: {
          employeeCode: row?.employeeCode,
          firstName: row?.firstName,
          middleName: row?.middleName,
          lastName: row?.lastName,
          mobileNumber: row?.mobileNumber,
          gender: row?.gender?.toUpperCase(),
          dob: row?.dob,
          companyEntityDisplayId: row?.companyEntityDisplayId,
          companyGroupId: '',
          reportToId: '',
          role: row?.roleCode,
          maritalStatus: row?.maritalStatus?.toUpperCase(),
          documentModels: [],
          alternateMobileNumber: row?.alternateMobileNumber,
          emailId: row?.emailId,
          secondaryEmailId: row?.secondaryEmailId,
          doj: row?.doj,
          nationality: row?.nationality,
          notificationPreferences: row?.notificationPreferences?.split(',') || ['EMAIL'],
          employeeEmploymentBasicDetails: {
            employeeId: '',
            branch: row?.branch,
            designation: row?.designation,
            location: row?.location,
            employmentType: row?.typeOfEmployment,
            departmentName: row?.departmentName,
            category: '',
            reasonForLeaving: row?.reasonForLeaving,
            dateOfLeaving: row?.dol
          },
          employeeStatutoryDetails: {
            employeeId: '',
            havePfAccount: '',
            pfNumber: row?.pfNumber,
            uanNumber: row?.uanNumber,
            haveEsiAccount: '',
            esiNumber: '',
            lwfApplicability: row?.epfApplicability,
            lwfNumber: '',
            nomineeName: row?.nomineeName,
            nomineeRelationship: row?.nomineeRelationship,
            nomineeDob: row?.nomineeDob,
            pan: row?.panNumber,
            aadhaar: row?.Aadhaar,
            currentPFAccountNumber: row?.currentPFAccountNumber
          },
          employeePayrollSummary: {
            employeeId: '',
            employeeCode: row?.employeeCode,
            esiNumber: row?.esiNumber,
            monthWorkingDays: row?.monthWorkingDays,
            workedDays: row?.workedDays,
            basicPay: row?.basicPay,
            hra: row?.hra,
            lta: row?.lta,
            perquisiteAllowance: row?.perquisiteAllowance,
            specialAllowance: row?.specialAllowance,
            telephoneAllowance: row?.telephoneAllowance,
            foodAllowance: row?.foodAllowance,
            grossEarnings: row?.grossEarnings,
            lop: row?.lop,
            tds: row?.tds,
            eps: row?.eps,
            advancePay: row?.advancePay,
            salaryDue: row?.salaryDue,
            natureOfWork: row?.natureOfWork,
            dailyHoursOfWork: row?.dailyHoursOfWork,
            intervalsOfRest: row?.intervalsOfRest,
            overtimeWages: row?.overtimeWages
          }
        },
        employeeComplianceReferenceDetailsOrchModel: {
          employeeCode: '',
          typeOfEmployment: row?.typeOfEmployment,
          epsApplicability: row?.epsApplicability,
          higherPensionApplicability: row?.higherPensionApplicability,
          dojInEPF: row?.dojInEPF,
          basicWages: row?.basicWages,
          grossWages: row?.grossWages,
          epfWageAsOnJoining: row?.epfWageAsOnJoining,
          nameInPAN: row?.nameInPAN,
          nameInAadhaar: row?.nameInAadhaar,
          passportNumber: row?.passportNumber,
          passportName: row?.passportName,
          passportExpiry: row?.passportExpiry,
          passportCountryOfIssue: row?.passportCountryOfIssue,
          fatherName: row?.fatherName,
          spouseName: row?.spouseName,
          physicallyHandicap: row?.physicallyHandicap,
          internationalWorker: row?.internationalWorker,
          dateOfExit: row?.dateOfExitFromPreviousEmployer,
          certificateNumber: row?.schemeCertificateNumber,
          passportValidFrom: row?.passportValidFrom,
          ppoNumber: row?.pensionPaymentOrderNumber,
          memberOfEmployeePensionScheme: '',
          havePfAccount: ''
        },
        employeeBankDetailsOrchModel: {
          ifscCode: row?.ifscCode,
          bankName: '',
          accountNumber: row?.accountNumber,
          accountType: '',
          bankBranchName: ''
        }
      },
      triggerWelcomeMail: row?.triggerWelcomeMail?.toUpperCase() === 'YES',
      triggerNewJoineeActivity: row?.triggerNewJoineeActivity === undefined || row?.triggerNewJoineeActivity === null || row?.triggerNewJoineeActivity === ''
        ? true
        : row?.triggerNewJoineeActivity?.toUpperCase() === 'YES'
    }
    employee.employeeWithReferenceDetailsModels.employeeOrchModel['address'] = this.createAddress(row)
    return employee
  }

  convertResponseAsPerExcel(response: any) {
    const responseList = []
    for (const row of response?.dataList) {
      const eo = row?.employeeWithReferenceDetailsModels?.employeeOrchModel
      const esd = eo?.employeeStatutoryDetails
      const address = eo?.address
      const { branch, departmentName, location } = eo?.employeeEmploymentBasicDetails || {}

      const {
        epsApplicability,
        dojInEPF,
        grossWages,
        nameInAadhaar,
        passportNumber,
        passportExpiry,
        passportCountryOfIssue,
        physicallyHandicap,
        internationalWorker,
        passportValidFrom,
        dol
      } = row?.employeeWithReferenceDetailsModels?.employeeComplianceReferenceDetailsOrchModel || {}

      const { ifscCode, accountNumber } = row?.employeeWithReferenceDetailsModels?.employeeBankDetailsOrchModel || {}

      responseList.push({
        'Entity ID': eo?.companyEntityDisplayId,
        'Employee ID': eo?.employeeCode,
        'First Name': eo?.firstName,
        'Middle Name': eo?.middleName,
        'Last Name': eo?.lastName,
        'Type Of Employee': eo?.typeOfEmployment,
        Department: departmentName,
        Branch: branch,
        Location: location,
        DOB: convertISOToDate(eo?.dob),
        Gender: eo?.gender?.toUpperCase(),
        'Date Of Joining': convertISOToDate(eo?.doj),
        'Marital Status': eo?.maritalStatus,
        NATIONALITY: eo?.nationality,
        'Primary Email ID': eo?.emailId,
        'Secondary Email ID': eo?.secondaryEmailId,
        'Primary Mobile Number': eo?.mobileNumber,
        'Secondary Mobile Number': eo?.alternateMobileNumber,
        'Aadhaar Number': esd?.aadhaar,
        'Aadhaar Name': nameInAadhaar,
        'PAN number': esd?.pan,
        'Pan Name': esd?.nameInPAN,
        'Nominee Name': esd?.nomineeName,
        'Nominee Relationship': esd?.nomineeRelationship,
        'Nominee DOB': convertISOToDate(esd?.nomineeDob),
        'UAN Number': esd?.uanNumber,
        'PF Number': esd?.pfNumber,
        'Current PF Account Number': esd?.currentPFAccountNumber,
        'Bank Account number': accountNumber,
        'IFSC Code': ifscCode,
        'Is International Worker': internationalWorker,
        'Passport Number': passportNumber,
        'Passport Validity From': convertISOToDate(passportValidFrom),
        'Passport Validity To': convertISOToDate(passportExpiry),
        'Country Of Issue': passportCountryOfIssue,
        'Is Physically Disabled': physicallyHandicap,
        'Basic Wages': grossWages,
        'EPF Joining Date': convertISOToDate(dojInEPF),
        'Date Of Leaving': convertISOToDate(dol),
        'EPF Applicability': esd?.lwfApplicability,
        'EPS Applicability': epsApplicability,
        'Gross Wages': grossWages,
        'Door No': address?.address1,
        Street: address?.address2,
        Area: address?.address3,
        City: address?.city,
        State: address?.state,
        Country: address?.country,
        PinCode: address?.pin,
        failed: row?.failed ? 'YES' : 'NO',
        message: row?.message,
        processed: row?.processed ? 'YES' : 'NO'
      })
    }
    return responseList
  }
}
